import type { JSX } from "react";

import { useIsCurrentOrgAdmin } from "@/api/queries/orgs";
import { AdminInsightNote } from "@/components/AdminInsightNote";
import { type UploadErrorAdminHint, uploadErrorAdminHint } from "@/lib/uploadErrors";

/**
 * KAN-1923 — owner instruction under an upload error that the shop's own
 * setup caused (no machine for the operation, order item limit).
 *
 * Renders nothing for anyone but a signed-in admin of the shop the page
 * belongs to — the customer sees only ``formatUploadError``'s sentence.
 * The admin check runs only once there IS something for the owner, so an
 * upload page without such an error does not ask who is looking.
 */
export function UploadErrorAdminInsight({
  error,
  lang,
}: {
  error: unknown;
  lang: string;
}): JSX.Element | null {
  const hint = uploadErrorAdminHint(error);
  if (!hint) return null;
  return <AdminOnlyHint hint={hint} lang={lang} />;
}

function AdminOnlyHint({
  hint,
  lang,
}: {
  hint: UploadErrorAdminHint;
  lang: string;
}): JSX.Element | null {
  const { isAdmin } = useIsCurrentOrgAdmin();
  if (!isAdmin) return null;
  return (
    <AdminInsightNote
      insightKey={hint.key}
      href={hint.href}
      lang={lang}
      testId="upload-error-admin-insight"
    />
  );
}
